import { safeFetch, getBaseUrl } from './api';

const url = (path: string) => `${getBaseUrl()}${path}`;

export interface InvoiceStats {
  total_invoices: number;
  paid_invoices: number;
  pending_invoices: number;
  partial_invoices: number;
  total_revenue: number;
  collected_amount: number;
  pending_amount: number;
  average_bill: number;
}

export interface RevenuePoint {
  date: string;
  invoice_count: number;
  collected_amount: number;
}

export interface CollectionRow {
  date: string;
  cash: number;
  upi: number;
  card: number;
  netbanking: number;
  cheque: number;
  advance: number;
  other: number;
  total: number;
}

export interface OutstandingRow {
  patient_id: string;
  patient_name: string;
  patient_mrn: string | null;
  phone: string | null;
  balance_due: number;
  last_transaction_date: string | null;
}

export interface DoctorRevenueRow {
  doctor_id: string | null;
  doctor_name: string | null;
  invoice_count: number;
  total_revenue: number;
  collected_amount: number;
  balance_due: number;
}

export interface AppointmentFootfallRow {
  date: string;
  total: number;
  completed: number;
  no_show: number;
  cancelled: number;
  upcoming: number;
}

export interface DoctorAppointmentRow {
  doctor_id: string | null;
  doctor_name: string | null;
  total_appointments: number;
  completed_count: number;
  no_show_count: number;
  cancelled_count: number;
}

export interface WardOccupancyRow {
  ward_id: string;
  ward_name: string;
  total_beds: number;
  occupied_beds: number;
}

export interface LengthOfStayStats {
  avgLengthOfStay: number | null;
  dischargedCount: number;
  activeAdmissions: number;
}

export async function getInvoiceStats(
  clinicId: string,
  opts?: { dateFrom?: string; dateTo?: string; doctorId?: string }
): Promise<InvoiceStats> {
  const params = new URLSearchParams();
  if (opts?.dateFrom) params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)   params.set('date_to', opts.dateTo);
  if (opts?.doctorId) params.set('doctor_id', opts.doctorId);
  const qs = params.toString();
  return safeFetch<InvoiceStats>(url(`/invoices/stats/${clinicId}${qs ? `?${qs}` : ''}`));
}

export async function getRevenueSummary(clinicId: string, days = 30): Promise<RevenuePoint[]> {
  return safeFetch<RevenuePoint[]>(url(`/invoices/revenue/${clinicId}?days=${days}`));
}

export async function getCollections(
  clinicId: string,
  opts?: { dateFrom?: string; dateTo?: string }
): Promise<CollectionRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.dateFrom) params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)   params.set('date_to', opts.dateTo);
  return safeFetch<CollectionRow[]>(url(`/reports/collections?${params.toString()}`));
}

export async function getOutstanding(clinicId: string, search?: string): Promise<OutstandingRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (search) params.set('search', search);
  return safeFetch<OutstandingRow[]>(url(`/reports/outstanding?${params.toString()}`));
}

export async function getRevenueByDoctor(
  clinicId: string,
  opts?: { dateFrom?: string; dateTo?: string }
): Promise<DoctorRevenueRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.dateFrom) params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)   params.set('date_to', opts.dateTo);
  return safeFetch<DoctorRevenueRow[]>(url(`/reports/by-doctor?${params.toString()}`));
}

export async function getAppointmentFootfall(
  clinicId: string,
  opts?: { dateFrom?: string; dateTo?: string }
): Promise<AppointmentFootfallRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.dateFrom) params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)   params.set('date_to', opts.dateTo);
  return safeFetch<AppointmentFootfallRow[]>(url(`/reports/appointments/footfall?${params.toString()}`));
}

export async function getAppointmentsByDoctor(
  clinicId: string,
  opts?: { dateFrom?: string; dateTo?: string }
): Promise<DoctorAppointmentRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.dateFrom) params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)   params.set('date_to', opts.dateTo);
  return safeFetch<DoctorAppointmentRow[]>(url(`/reports/appointments/by-doctor?${params.toString()}`));
}

export interface PatientDailyRow {
  date: string;
  new_patients: number;
}

export interface PatientSummary {
  total_patients: number;
  new_patients: number;
  returning_patients: number;
  avg_visits: number;
}

export interface PatientDoctorRow {
  doctor_id: string | null;
  doctor_name: string | null;
  patient_count: number;
  new_count: number;
  returning_count: number;
}

export interface PatientStatsResult {
  daily: PatientDailyRow[];
  summary: PatientSummary;
  byDoctor: PatientDoctorRow[];
}

export async function getPatientStats(
  clinicId: string,
  opts?: { dateFrom?: string; dateTo?: string }
): Promise<PatientStatsResult> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.dateFrom) params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)   params.set('date_to', opts.dateTo);
  return safeFetch<PatientStatsResult>(url(`/reports/patients?${params.toString()}`));
}

export async function getOccupancySummary(clinicId: string): Promise<WardOccupancyRow[]> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  return safeFetch<WardOccupancyRow[]>(url(`/reports/ipd/occupancy?${params.toString()}`));
}

export async function getLengthOfStay(
  clinicId: string,
  opts?: { dateFrom?: string; dateTo?: string }
): Promise<LengthOfStayStats> {
  const params = new URLSearchParams({ clinic_id: clinicId });
  if (opts?.dateFrom) params.set('date_from', opts.dateFrom);
  if (opts?.dateTo)   params.set('date_to', opts.dateTo);
  return safeFetch<LengthOfStayStats>(url(`/reports/ipd/length-of-stay?${params.toString()}`));
}
